import { useCallback, useEffect, useRef, useState } from 'react'
import { pull, push } from '../lib/api'
import { collectDirty } from '../lib/sync'
import { useStore } from './store'

/** Background sync cadence while the tab is open and visible. */
const INTERVAL_MS = 60_000
/** Debounce after a local change, so a burst of edits sends once. */
const DEBOUNCE_MS = 3_000
/** Safety valve on the pull loop, in case a server keeps claiming hasMore. */
const MAX_PAGES = 50

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error'

/**
 * Drives push-then-pull against the server.
 *
 * Push comes first so that a device which has been working offline gets its own
 * changes onto the server before it learns about anyone else's — otherwise a
 * pull could hand it a stale copy of a record it has already edited, and the
 * merge would have to unwind it.
 *
 * Only one run happens at a time. Overlapping syncs would race on the cursor
 * and could advance it past changes that were never applied.
 */
export function useSync() {
  const { data, dispatch } = useStore()
  const [phase, setPhase] = useState<SyncPhase>('idle')
  const running = useRef(false)

  // Read through a ref so the callback identity stays stable and the effects
  // below don't tear down and restart their timers on every keystroke.
  const dataRef = useRef(data)
  dataRef.current = data

  const sync = useCallback(async () => {
    const current = dataRef.current
    const token = current.sync.token
    if (!token || running.current) return

    running.current = true
    setPhase('syncing')

    try {
      const dirty = collectDirty(current)
      if (dirty.length) {
        const result = await push(dirty, token)
        if (!result.ok) {
          setPhase(result.offline ? 'offline' : 'error')
          dispatch({ type: 'patchSync', patch: { lastError: result.error } })
          if (result.code === 'unauthorized') dispatch({ type: 'signOut' })
          return
        }
        // Mark clean only up to the newest record actually sent. Using "now"
        // would silently swallow any edit made while the request was in flight.
        const pushedUpTo = dirty.reduce((max, r) => Math.max(max, r.updatedAt), 0)
        dispatch({ type: 'patchSync', patch: { lastPushedAt: pushedUpTo } })
      }

      let cursor = dataRef.current.sync.cursor
      for (let page = 0; page < MAX_PAGES; page++) {
        const result = await pull(cursor, token)
        if (!result.ok) {
          setPhase(result.offline ? 'offline' : 'error')
          dispatch({ type: 'patchSync', patch: { lastError: result.error } })
          if (result.code === 'unauthorized') dispatch({ type: 'signOut' })
          return
        }

        if (result.records.length) {
          dispatch({ type: 'mergeRemote', records: result.records, cursor: result.cursor })
        } else {
          dispatch({ type: 'patchSync', patch: { cursor: result.cursor, lastSyncAt: Date.now(), lastError: null } })
        }

        cursor = result.cursor
        if (!result.hasMore) break
      }

      setPhase('idle')
    } finally {
      running.current = false
    }
  }, [dispatch])

  const token = data.sync.token
  // Derived, not stored: the count of records still waiting to go up.
  const pendingCount = token ? collectDirty(data).length : 0

  // Sync on sign-in, and whenever the network or tab comes back.
  useEffect(() => {
    if (!token) return
    void sync()

    const onOnline = () => void sync()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sync()
    }

    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(() => void sync(), INTERVAL_MS)

    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [token, sync])

  // Debounced push after local edits.
  useEffect(() => {
    if (!token || pendingCount === 0) return
    const timer = window.setTimeout(() => void sync(), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [token, pendingCount, sync])

  return {
    phase,
    pendingCount,
    lastSyncAt: data.sync.lastSyncAt,
    lastError: data.sync.lastError,
    signedIn: Boolean(token),
    deviceName: data.sync.deviceName,
    syncNow: sync,
  }
}
